import { useState } from 'react';
import {
  Mail, StickyNote, Star, Flag, TrendingUp, CalendarCheck, ClipboardList, Target, ChevronRight, CalendarPlus, MessageSquare, FileText,
} from 'lucide-react';
import { toast } from 'sonner';
import { MessagesDialog } from '@/features/OrgLayout/student/MessagesDialog';
import { StudentEvidenceDrawer } from '@/features/OrgLayout/student/StudentEvidenceDrawer';
import { StudentCompanyFit } from '@/features/OrgLayout/student/StudentCompanyFit';
import { StudentWorkCard } from '@/features/OrgLayout/student/StudentWorkCard';
import { openResume } from '@/lib/api/auth';
import { cn } from '@/lib/cn';
import { CircularGauge } from '@/components/ui/circular-gauge';
import { OrgCard } from '@/features/OrgLayout/primitives/OrgCard';
import { OrgAvatar } from '@/features/OrgLayout/primitives/OrgAvatar';
import { OrgProgressBar } from '@/features/OrgLayout/primitives/OrgProgressBar';
import { Sparkline } from '@/features/OrgLayout/primitives/Sparkline';
import { ReadinessBadge } from '@/features/OrgLayout/ReadinessBadge';
import { ReadinessAsOf, ReadinessBreakdown } from '@/features/Readiness';
import { atRiskThresholdFrom, ReadinessTrendChart } from '@/features/Review';
import { useReadinessFormula } from '@/lib/hooks/useReadiness';
import { useOrgReadinessEvidence, useOrgStudentReadinessHistory } from '@/lib/hooks/useOrg';
import type { OrgStudentDetail } from '@/lib/api/org';
import type { ReadinessEvidenceItem } from '@/lib/api/readiness';

export interface StudentDetailViewProps {
  data: OrgStudentDetail;
  onAddNote: (content: string) => void;
  addNotePending: boolean;
  onToggleShortlist: () => void;
  onToggleFlag: () => void;
  shortlistPending: boolean;
  flagPending: boolean;
  /**
   * Client-side navigation for the evidence deep links. Optional so this view
   * can still be rendered outside a router (tests, storybook) — without it the
   * links are ordinary anchors.
   */
  onNavigate?: (href: string) => void;
}

const METRIC_TONE = { teal: '#0f766e', orange: '#ff5a1f', blue: '#2563eb', purple: '#7c3aed' } as const;

/**
 * Routes for the records behind a readiness component. The API sends a kind
 * and an id and no URL at all, on purpose: routing lives here, so a route move
 * is a compile-time concern rather than a link that silently 404s.
 *
 * `problem` has no org-side route, so those items stay plain text rather than
 * becoming a link to nowhere.
 */
function evidenceHref(batchId: string, item: ReadinessEvidenceItem): string | undefined {
  if (!item.link_id) return undefined;
  switch (item.link_kind) {
    case 'assessment':
      return `/org/batches/${batchId}/assessments/${item.link_id}`;
    case 'assignment':
      return `/org/batches/${batchId}/assignments/${item.link_id}`;
    case 'session':
      return `/org/batches/${batchId}/sessions/${item.link_id}`;
    default:
      return undefined;
  }
}

export function StudentDetailView({
  data, onAddNote, addNotePending, onToggleShortlist, onToggleFlag, shortlistPending, flagPending,
  onNavigate,
}: StudentDetailViewProps) {
  const [note, setNote] = useState('');
  const [msgOpen, setMsgOpen] = useState(false);
  const [wantEvidence, setWantEvidence] = useState(false);
  const [drillTarget, setDrillTarget] = useState<{ key: string; label: string } | null>(null);
  const skills = Object.entries(data.skill_scores);
  // A TPO has to be able to justify this number to a student; the formula and
  // the student's own breakdown are served by the API so this view and the
  // student's own view can never disagree.
  const { data: formula } = useReadinessFormula();
  // Constant-size: seven components, each with its distribution and its five
  // worst records. The full list behind any one of them is a separate, paged
  // request the drawer makes only when a TPO actually opens it.
  const { data: evidence, isFetching: evidenceLoading } = useOrgReadinessEvidence(
    data.batch_id,
    data.user_id,
    { enabled: wantEvidence },
  );
  // The trend is small and always wanted on this page — unlike the evidence,
  // which is gated behind an expand because it can be hundreds of records.
  const { data: history, isLoading: historyLoading } = useOrgStudentReadinessHistory(
    data.batch_id,
    data.user_id,
  );
  const scored = data.breakdown?.has_data !== false;

  const metrics = [
    { label: 'Progress', value: `${Math.round(data.completion_pct)}%`, median: data.benchmarks.completion_median, icon: TrendingUp, tone: METRIC_TONE.teal },
    { label: 'Attendance', value: `${Math.round(data.attendance_pct)}%`, median: data.benchmarks.attendance_median, icon: CalendarCheck, tone: METRIC_TONE.orange },
    { label: 'Avg Score', value: `${Math.round(data.avg_score)}`, median: data.benchmarks.avg_score_median, icon: ClipboardList, tone: METRIC_TONE.blue },
    { label: 'Readiness', value: `${Math.round(data.readiness_score)}`, median: data.benchmarks.readiness_median, icon: Target, tone: METRIC_TONE.purple },
  ];

  const viewResume = async () => {
    try {
      const url = await openResume(data.user_id);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch {
      toast.info(`${data.name} hasn't uploaded a resume yet.`);
    }
  };

  const scheduleMeeting = () => {
    const url = new URL('https://calendar.google.com/calendar/render');
    url.searchParams.set('action', 'TEMPLATE');
    url.searchParams.set('text', `Meeting with ${data.name}`);
    url.searchParams.set('add', data.email);
    url.searchParams.set('details', `Placement mentoring session for ${data.name} (${data.batch_name}).`);
    window.open(url.toString(), '_blank', 'noopener,noreferrer');
  };

  const submitNote = () => {
    const c = note.trim();
    if (!c) return;
    onAddNote(c);
    setNote('');
  };

  return (
    <div className="space-y-5">
      {/* Identity + readiness + actions */}
      <div className="grid gap-5 lg:grid-cols-[1fr_300px]">
        <OrgCard className="p-6">
          <div className="flex items-start gap-4">
            <OrgAvatar name={data.name} size={56} />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold text-[#111827]">{data.name}</h1>
                <ReadinessBadge band={data.readiness_band} />
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-[#6b7280]">
                <span>{data.batch_name}</span>
                <span className="inline-flex items-center gap-1.5"><Mail className="h-4 w-4 text-[#9ca3af]" /> {data.email}</span>
              </div>
            </div>
          </div>

          {/* Student summary */}
          {data.verdict && (
            <div className="mt-5 rounded-2xl bg-[#fafafa] p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-[#9ca3af]">Student summary</p>
              <p className="mt-1.5 text-sm leading-relaxed text-[#374151]">{data.verdict}</p>
            </div>
          )}
        </OrgCard>

        <OrgCard className="flex flex-col items-center justify-center p-6 text-center">
          <p className="text-sm font-medium text-[#6b7280]">Placement Readiness</p>
          {scored ? (
            <>
              <div className="mt-2"><CircularGauge value={data.readiness_score} label={`Top ${Math.max(1, 100 - Math.round(data.percentile))}%`} unit="/100" color="#ff5a1f" size={110} /></div>
              <p className="mt-1 text-xs text-[#9ca3af]">Rank {data.batch_rank} of {data.batch_size}</p>
            </>
          ) : (
            // Never a gauge at 0: no measurable work is not the same as failing.
            <p className="mt-3 text-sm text-[#6b7280]">
              Not enough data yet — this student has no measurable work, so there is no readiness
              score to show.
            </p>
          )}
          <ReadinessAsOf computedAt={data.computed_at} className="text-[#9ca3af]" />
        </OrgCard>
      </div>

      {/*
        Is this student improving, flat or slipping? The gauge above answers
        "where are they"; only this answers "which way are they going", which
        is the question a review cycle actually turns on.
      */}
      <OrgCard className="p-6">
        <ReadinessTrendChart
          trend={history}
          loading={historyLoading}
          atRiskThreshold={atRiskThresholdFrom(formula)}
        />
      </OrgCard>

      {/* Why this score — the same breakdown the student sees */}
      <OrgCard className="p-6">
        <h3 className="text-lg font-semibold text-[#111827]">Why this score</h3>
        <p className="mt-1 text-xs text-[#9ca3af]">
          Each input shows what it is made of. Expand one for the records costing the most, or
          open the full list to search and filter it.
        </p>
        <ReadinessBreakdown
          className="mt-3"
          title={`How ${data.name.split(' ')[0]}'s readiness is made up`}
          breakdown={data.breakdown}
          formula={formula}
          computedAt={data.computed_at}
          evidence={evidence}
          evidenceLoading={evidenceLoading}
          onRequestEvidence={() => setWantEvidence(true)}
          onViewAll={(key, label) => setDrillTarget({ key, label })}
          linkFor={(item) => evidenceHref(data.batch_id, item)}
          onNavigate={onNavigate}
        />
      </OrgCard>

      <StudentEvidenceDrawer
        batchId={data.batch_id}
        userId={data.user_id}
        target={drillTarget}
        onClose={() => setDrillTarget(null)}
        linkFor={(item) => evidenceHref(data.batch_id, item)}
        onNavigate={(href) => {
          // Following a record means leaving this page; the drawer would
          // otherwise stay mounted over whatever the link opened.
          setDrillTarget(null);
          onNavigate?.(href);
        }}
      />

      {/* Actions (real mutations only) */}
      <OrgCard className="flex flex-wrap items-center gap-3 p-4">
        <span className="mr-1 text-sm font-medium text-[#6b7280]">Actions</span>
        <ActionButton icon={MessageSquare} label="Message" onClick={() => setMsgOpen(true)} />
        <ActionButton icon={FileText} label="View resume" onClick={viewResume} />
        <ActionButton icon={StickyNote} label="Add note" onClick={() => document.getElementById('org-note-input')?.focus()} />
        <ActionButton icon={CalendarPlus} label="Schedule meeting" onClick={scheduleMeeting} />
        <ActionButton
          icon={Star}
          label={data.shortlisted ? 'Shortlisted' : 'Shortlist'}
          active={data.shortlisted}
          pending={shortlistPending}
          onClick={onToggleShortlist}
        />
        <ActionButton
          icon={Flag}
          label={data.flagged ? 'Flagged' : 'Flag'}
          active={data.flagged}
          activeTone="#dc2626"
          pending={flagPending}
          onClick={onToggleFlag}
        />
      </OrgCard>

      <MessagesDialog studentId={data.user_id} studentName={data.name} open={msgOpen} onOpenChange={setMsgOpen} />

      {/* Metric tiles */}
      <div className="grid grid-cols-2 gap-5 lg:grid-cols-4">
        {metrics.map((m) => (
          <OrgCard key={m.label} className="p-5">
            <div className="flex items-center gap-2">
              <m.icon className="h-4 w-4" style={{ color: m.tone }} />
              <span className="text-sm font-medium text-[#6b7280]">{m.label}</span>
            </div>
            <p className="mt-2 text-3xl font-bold text-[#111827]">{m.value}</p>
            <p className="mt-1 text-xs text-[#9ca3af]">batch median {Math.round(m.median)}</p>
          </OrgCard>
        ))}
      </div>

      {/* Trends (6-week, from real per-student series) */}
      {data.trends && (data.trends.avg_score.length >= 2 || data.trends.activity.length >= 2 || data.trends.completion.length >= 2) && (
        <OrgCard className="p-6">
          <h3 className="text-lg font-semibold text-[#111827]">Trends</h3>
          <p className="mt-1 text-xs text-[#9ca3af]">Last 6 weeks</p>
          <div className="mt-4 grid gap-6 sm:grid-cols-3">
            <TrendCell label="Avg score / week" values={data.trends.avg_score.map((p) => p.value)} stroke="#2563eb" />
            <TrendCell label="Weekly activity" values={data.trends.activity.map((p) => p.value)} stroke="#ff5a1f" />
            <TrendCell label="Completions / week" values={data.trends.completion.map((p) => p.value)} stroke="#0f766e" />
          </div>
        </OrgCard>
      )}

      {/* Skills + the work itself */}
      <div className="grid gap-5 lg:grid-cols-[minmax(0,340px)_minmax(0,1fr)]">
        <OrgCard className="p-6">
          <h3 className="text-lg font-semibold text-[#111827]">Skills</h3>
          {skills.length === 0 ? (
            <p className="py-8 text-center text-sm text-[#9ca3af]">No skill data yet.</p>
          ) : (
            <ul className="mt-4 space-y-3.5">
              {skills.map(([skill, score]) => (
                <li key={skill}>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-[#374151]">{skill}</span>
                    <span className="font-semibold tabular-nums text-[#111827]">{Math.round(score)}%</span>
                  </div>
                  <OrgProgressBar pct={score} className="mt-1.5" barClassName="bg-[#0f766e]" />
                </li>
              ))}
            </ul>
          )}
        </OrgCard>

        <StudentWorkCard
          batchId={data.batch_id}
          userId={data.user_id}
          studentName={data.name}
          batchStudentCount={data.batch_size}
        />
      </div>

      <StudentCompanyFit userId={data.user_id} />

      {/* Private notes */}
      <OrgCard className="p-6">
        <div className="flex items-center gap-2">
          <h3 className="text-lg font-semibold text-[#111827]">Private Notes</h3>
          <span className="rounded-full bg-[#fff1ec] px-2.5 py-0.5 text-xs font-medium text-[#ff5a1f]">Visible only to TPOs</span>
        </div>
        {data.notes.length > 0 && (
          <ul className="mt-4 space-y-3">
            {data.notes.map((n) => (
              <li key={n.id} className="rounded-2xl bg-[#fafafa] p-4">
                <p className="text-sm leading-relaxed text-[#374151]">{n.content}</p>
                <p className="mt-2 text-xs text-[#9ca3af]">Added by {n.created_by} · {n.created_at}</p>
              </li>
            ))}
          </ul>
        )}
        <div className="mt-4">
          <textarea
            id="org-note-input"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Add a private note about this student..."
            rows={3}
            className="w-full resize-none rounded-2xl border border-[#ececec] bg-white p-3 text-sm text-[#111827] placeholder:text-[#9ca3af] focus:border-[#ff5a1f] focus:outline-none"
          />
          <div className="mt-2 flex justify-end">
            <button
              type="button"
              onClick={submitNote}
              disabled={addNotePending || !note.trim()}
              className="rounded-[14px] bg-[#ff5a1f] px-4 py-2 text-sm font-semibold text-white disabled:opacity-40"
            >
              {addNotePending ? 'Saving…' : 'Save note'}
            </button>
          </div>
        </div>
      </OrgCard>
    </div>
  );
}

function TrendCell({ label, values, stroke }: { label: string; values: number[]; stroke: string }) {
  const latest = values.length ? Math.round(values[values.length - 1]) : 0;
  return (
    <div>
      <div className="flex items-baseline justify-between">
        <span className="text-xs font-medium text-[#6b7280]">{label}</span>
        <span className="text-sm font-bold text-[#111827]">{latest}</span>
      </div>
      <div className="mt-2">
        {values.length >= 2 ? (
          <Sparkline values={values} stroke={stroke} width={260} height={48} className="w-full" />
        ) : (
          <p className="py-4 text-xs text-[#9ca3af]">Not enough data yet.</p>
        )}
      </div>
    </div>
  );
}

function ActionButton({
  icon: Icon, label, onClick, active, activeTone = '#ff5a1f', pending,
}: {
  icon: typeof Mail; label: string; onClick: () => void; active?: boolean; activeTone?: string; pending?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={pending}
      className={cn(
        'inline-flex items-center gap-2 rounded-[14px] border px-3.5 py-2 text-sm font-medium transition-colors disabled:opacity-50',
        active ? 'border-transparent text-white' : 'border-[#ececec] bg-white text-[#111827] hover:bg-[#fafafa]',
      )}
      style={active ? { backgroundColor: activeTone } : undefined}
    >
      <Icon className="h-4 w-4" />
      {label}
    </button>
  );
}
